"use client";

import { motion } from "motion/react";
import { Waitlist } from "@clerk/nextjs";
import { HoverCard } from "./hover-card";

const rise = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.8, ease: [0.22, 1, 0.36, 1] as const },
  },
});

const PROPERTIES = [
  { name: "Lincoln Park", noi: "$482K", cap: "7.2%", bar: 72 },
  { name: "River North", noi: "$310K", cap: "6.4%", bar: 64 },
  { name: "Bucktown", noi: "$275K", cap: "6.8%", bar: 68 },
];

const MEMORY = [
  {
    label: "Lincoln Park acquisition memo",
    date: "2 days ago",
    color: "#c9a46a",
  },
  {
    label: "Q2 DSCR stress test · all pass",
    date: "1 week ago",
    color: "#5be3c7",
  },
  {
    label: "LP quarterly update prepared",
    date: "3 weeks ago",
    color: "#818cf8",
  },
];

export function LandingPage() {
  return (
    <div className="min-h-screen bg-[#06070f] text-[#ede9e0]">
      {/* ── Nav ── */}
      <nav className="mx-auto flex w-full max-w-[1400px] items-center justify-between px-[5vw] py-5 sm:px-[6vw] sm:py-7">
        <motion.span
          className="font-serif text-[1.05rem] font-bold tracking-[-0.01em] text-[#ede9e0]"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          PropCentral<span style={{ color: "#c9a46a" }}>.ai</span>
        </motion.span>
        <motion.a
          href="#waitlist"
          className="font-mono text-[0.68rem] uppercase tracking-[0.14em] text-[#4a4a62] transition-colors duration-50 hover:text-[#c9a46a]"
          whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
        >
          Apply →
        </motion.a>
      </nav>

      {/* ── Hero ── */}
      <section className="relative mx-auto flex w-full max-w-[1400px] flex-col justify-center overflow-hidden px-[5vw] py-14 sm:px-[6vw] sm:py-0 min-h-[91vh]">
        <div className="absolute top-15 left-[-5%] w-[55vw] h-[55vw] bg-[radial-gradient(circle,rgba(100,100,200,0.055)_0%,transparent_68%)] blur-[60px] pointer-events-none" />

        <motion.p
          {...rise(0.1)}
          className="mb-6 font-mono text-[0.68rem] uppercase tracking-[0.2em] text-[#3a3a55] sm:mb-10"
        >
          Real estate intelligence — early access
        </motion.p>

        <motion.h1
          {...rise(0.25)}
          className="mb-6 max-w-[18ch] font-serif text-[clamp(3.6rem,8.5vw,7.8rem)] font-bold leading-[1.05] tracking-[-0.025em] text-[#ede9e0] sm:mb-10 sm:max-w-[21ch] sm:leading-[1]"
        >
          Excel stays.
          <br />
          <em style={{ color: "#c9a46a", fontStyle: "italic" }}>
            Busywork
          </em>{" "}
          goes.
        </motion.h1>

        <motion.p
          {...rise(0.4)}
          className="mb-10 max-w-[40ch] text-[0.98rem] leading-[1.75] text-[#44445a] sm:mb-14 sm:text-[1.05rem] sm:leading-[1.8]"
        >
          Your team spends time on investment decisions, not data handling.
        </motion.p>

        <motion.a
          {...rise(0.55)}
          href="#waitlist"
          className="inline-flex w-fit items-center gap-2 border-b border-b-[rgba(201,164,106,0.25)] pb-1 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[#c9a46a]"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          Join the waitlist <span>→</span>
        </motion.a>

        <div className="absolute bottom-12 left-[6vw] hidden h-[52px] w-px bg-[linear-gradient(to_bottom,transparent,rgba(74,74,98,0.35))] sm:block" />
      </section>

      {/* ── Feature Bento ── */}
      <section className="mx-auto w-full max-w-[1400px] px-[5vw] pb-28 sm:px-[6vw] sm:pb-40">
        {/* Section label */}
        <div className="mb-8 flex items-center gap-5">
          <motion.span
            className="shrink-0 font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[#2e2e45]"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What you get
          </motion.span>
          <motion.div
            className="h-px flex-1 bg-[rgba(255,255,255,0.05)]"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ transformOrigin: "left" }}
          ></motion.div>
        </div>

        {/* Bento grid */}
        <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-3.5 p-5 overflow-visible">
          {/* ── Card 1: Instant Query (full width) ── */}
          <HoverCard
            parentClassName="md:col-span-2 md:col-span-1 p-5"
            className="relative col-span-full grid grid-cols-1 items-center gap-6 overflow-hidden rounded-[20px] border border-[rgba(255,255,255,0.07)] bg-[#0b0c1a] shadow-xl p-10 my-2 lg:grid-cols-2 lg:gap-16"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Glow */}
            <div className="absolute top-[-10%] right-[5%] w-[45%] h-[90%] pointer-events-none bg-[radial-gradient(circle,rgba(201,164,106,0.06)_0%,transparent_70%)] blur-[50px]" />

            {/* Left: copy */}
            <div className="relative">
              <p className="mb-5 font-mono text-[0.62rem] uppercase tracking-[0.18em] text-[#3a3a55]">
                Instant insights
              </p>
              <h3 className="mb-4 font-serif text-[clamp(1.7rem,2.6vw,2.4rem)] font-bold leading-[1.1] tracking-[-0.02em] text-[#ddd8cc]">
                Any metric.
                <br />
                In seconds.
              </h3>
              <p className="text-[0.9rem] leading-[1.85] text-[#3e3e5a]">
                Ask anything across your entire portfolio in plain English. No
                spreadsheet diving, no waiting on analysts.
              </p>
            </div>

            {/* Right: UI mockup */}
            <div className="rounded-[14px] border border-[rgba(255,255,255,0.05)] bg-[#070810] p-4 sm:p-6">
              {/* Chrome dots */}
              <div className="mb-5 flex gap-1.5">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-2 w-2 rounded-full bg-[rgba(255,255,255,0.08)]"
                  />
                ))}
              </div>

              {/* Query bar */}
              <div className="mb-6 rounded-[8px] border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] px-4 py-3 font-mono text-[0.72rem] leading-[1.5] text-[#ede9e0]">
                <span style={{ color: "#3a3a55" }}>→ </span>
                What's the blended cap rate across all multifamily?
              </div>

              {/* Answer */}
              <div className="mb-5 flex items-baseline gap-3">
                <span className="font-serif text-[2.6rem] font-bold leading-none text-[#c9a46a] sm:text-[2.8rem]">
                  6.8%
                </span>
                <span className="font-mono text-[0.6rem] tracking-[0.05em] text-[#3a3a55]">
                  blended · Q3 2025 · ↑ 0.4%
                </span>
              </div>

              {/* Breakdown bars */}
              {PROPERTIES.map(({ name, cap, bar }, i) => (
                <div
                  key={name}
                  className="mb-2 grid grid-cols-[74px_1fr_34px] items-center gap-2.5 sm:grid-cols-[88px_1fr_36px]"
                >
                  <span className="font-mono text-[0.6rem] text-[#3a3a55]">
                    {name}
                  </span>
                  <div className="h-[3px] overflow-hidden rounded-[2px] bg-[rgba(255,255,255,0.05)]">
                    <motion.div
                      className="h-full bg-[rgba(201,164,106,0.45)] rounded-[2px]"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${bar}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: i * 0.1 }}
                    ></motion.div>
                  </div>
                  <span className="text-right font-mono text-[0.6rem] text-[#c9a46a]">
                    {cap}
                  </span>
                </div>
              ))}
            </div>
          </HoverCard>

          {/* ── Card 2: Dynamic Reporting ── */}
          <HoverCard
            parentClassName="col-span-1 px-5"
            className="flex flex-col overflow-hidden rounded-[20px] border border-[rgba(255,255,255,0.07)] bg-[#0b0c1a] shadow-xl p-10 my-2"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{
              delay: 0.08,
              duration: 0.75,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <p className="mb-5 font-mono text-[0.62rem] uppercase tracking-[0.18em] text-[#3a3a55]">
              Dynamic reporting
            </p>
            <h3 className="mb-3 font-serif text-[clamp(1.5rem,2vw,2rem)] font-bold leading-[1.15] tracking-[-0.02em] text-[#ddd8cc]">
              Reports that evolve
              <br />
              with your strategy.
            </h3>
            <p className="mb-8 text-[0.875rem] leading-[1.85] text-[#3e3e5a]">
              Add new tracking metrics across all properties instantly — no
              rebuild required.
            </p>

            {/* Table mockup */}
            <div className="flex-1 overflow-hidden rounded-[12px] border border-[rgba(255,255,255,0.05)] bg-[#070810]">
              {/* Header */}
              <div className="grid grid-cols-[1fr_58px_58px] border-b border-b-[rgba(255,255,255,0.05)] px-3.5 py-2.5 sm:grid-cols-[1fr_72px_72px] sm:px-[1.1rem]">
                {["Property", "NOI", "Cap Rate"].map((h) => (
                  <span
                    key={h}
                    className="font-mono text-[0.57rem] uppercase tracking-[0.12em] text-[#2a2a42]"
                  >
                    {h}
                  </span>
                ))}
              </div>

              {/* Rows */}
              {PROPERTIES.map(({ name, noi, cap }, i) => (
                <div
                  key={name}
                  className="grid grid-cols-[1fr_58px_58px] items-center px-3.5 py-2.5 sm:grid-cols-[1fr_72px_72px] sm:px-[1.1rem]"
                  style={{
                    borderBottom:
                      i < PROPERTIES.length - 1
                        ? "1px solid rgba(255,255,255,0.03)"
                        : "none",
                  }}
                >
                  <span className="font-mono text-[0.67rem] text-[#6a6a88]">
                    {name}
                  </span>
                  <span className="font-mono text-[0.67rem] text-[#6a6a88]">
                    {noi}
                  </span>
                  <span className="font-mono text-[0.67rem] text-[#c9a46a]">
                    {cap}
                  </span>
                </div>
              ))}

              {/* Add metric row */}
              <div className="flex items-center gap-2 border-t border-t-[rgba(255,255,255,0.03)] px-3.5 py-2.5 sm:px-[1.1rem]">
                <span className="text-[0.9rem] leading-none text-[rgba(201,164,106,0.35)]">
                  +
                </span>
                <span className="font-mono text-[0.6rem] tracking-[0.08em] text-[#2a2a42]">
                  Add metric
                </span>
              </div>
            </div>
          </HoverCard>

          {/* ── Card 3: Institutional Memory ── */}
          <HoverCard
            parentClassName="col-span-1 px-5"
            className="flex flex-col overflow-hidden rounded-[20px] border border-[rgba(255,255,255,0.07)] bg-[#0b0c1a] shadow-xl p-10 my-2"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{
              delay: 0.18,
              duration: 0.75,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <p className="mb-5 font-mono text-[0.62rem] uppercase tracking-[0.18em] text-[#3a3a55]">
              Institutional memory
            </p>
            <h3 className="mb-3 font-serif text-[clamp(1.5rem,2vw,2rem)] font-bold leading-[1.15] tracking-[-0.02em] text-[#ddd8cc]">
              Knowledge that
              <br />
              never leaves.
            </h3>
            <p className="mb-8 text-[0.875rem] leading-[1.85] text-[#3e3e5a]">
              Every analysis, every decision captured. No more knowledge walking
              out the door.
            </p>

            {/* Timeline mockup */}
            <motion.div
              className="flex flex-1 flex-col gap-2.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ staggerChildren: 0.1, delayChildren: 0.2 }}
            >
              {MEMORY.map(({ label, date, color }) => (
                <motion.div
                  key={label}
                  className="flex items-start gap-3.5 rounded-[10px] border border-[rgba(255,255,255,0.05)] bg-[#070810] px-4 py-3.5"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div
                    className="mt-[3px] h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ background: color }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="mb-[3px] overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[0.67rem] text-[#6a6a88]">
                      {label}
                    </p>
                    <p className="font-mono text-[0.58rem] text-[#2a2a42]">
                      {date}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </HoverCard>
        </div>
      </section>

      {/* ── Waitlist ── */}
      <section
        id="waitlist"
        className="relative overflow-hidden border-t border-t-[rgba(255,255,255,0.05)]"
      >
        {/* Glow */}
        <div className="absolute top-[50%] left-[50%] w-[70vw] h-[70vw] pointer-events-none bg-[radial-gradient(circle,rgba(100,100,200,0.04)_0%,transparent_65%)] blur-[80px]" />

        <div className="relative mx-auto flex w-full max-w-[1400px] flex-col items-center px-[5vw] py-24 pb-28 text-center sm:px-[6vw] sm:py-32 sm:pb-40">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-6 font-mono text-[0.62rem] uppercase tracking-[0.22em] text-[#2e2e45]"
          >
            Early access
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="mb-5 max-w-[16ch] font-serif text-[clamp(2.2rem,4.5vw,3.8rem)] font-bold leading-[1.05] tracking-[-0.025em] text-[#ede9e0]"
          >
            Built for firms that
            <br />
            <em style={{ color: "#c9a46a", fontStyle: "italic" }}>
              think in deals.
            </em>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="mb-14 max-w-[38ch] text-[0.95rem] leading-[1.85] text-[#3e3e5a]"
          >
            We're onboarding a small group of investment firms. Join the
            waitlist for early access.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.02 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="w-full max-w-[400px]"
          >
            <Waitlist />
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="mx-auto flex w-full max-w-[1400px] items-center justify-between border-t border-t-[rgba(255,255,255,0.04)] px-[5vw] py-6 sm:px-[6vw] sm:py-8">
        <motion.span
          className="font-serif text-[0.9rem] font-bold tracking-[-0.01em] text-[#22223a]"
          whileHover={{ scale: 1.05 }}
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          PropCentral<span style={{ color: "rgba(201,164,106,0.3)" }}>.ai</span>
        </motion.span>
        <motion.span
          className="font-mono text-[0.6rem] text-[#1e1e30]"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          2025
        </motion.span>
      </footer>
    </div>
  );
}
